import { useState, useEffect } from 'react';

import { Container } from '@app/components/Container';
import { PageHeader } from '@app/components/PageHeader';
import { ErrorAlert } from '@app/components/ErrorAlert';
import { useErrorHandling } from '@app/hooks/useErrorHandling';
import { CreateKeyResponse } from '@app/api/api-keys/types';

import { CreateKeyModal } from './components/CreateKeyModal';
import { ApiKeyCreatedModal } from './components/ApiKeyCreatedModal';
import { KeysHeader } from './components/KeysHeader';
import { KeysList } from './components/KeysList';
import { KeysTable } from './components/KeysTable';
import { useKeyManagement, CreateKeyForm } from './hooks/useKeyManagement';
import { useKeyDeletion } from './hooks/useKeyDeletion';

export default function ApiKeysPage() {
  const { keys, keyLimit, isLoading, createKey, fetchKeys } = useKeyManagement();
  const { deleteKey, cancelDelete, pendingDeletions, deletingKeys } = useKeyDeletion();
  const { error, handleError, clearError } = useErrorHandling();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newKey, setNewKey] = useState<CreateKeyResponse | null>(null);

  // Fetch keys on mount
  useEffect(() => {
    fetchKeys().catch(handleError);
  }, [fetchKeys, handleError]);

  // Handle key creation
  const handleCreateKey = async (formData: CreateKeyForm) => {
    try {
      const createdKey = await createKey(formData);
      setNewKey(createdKey);
      setIsCreateModalOpen(false);
    } catch (err) {
      handleError(err);
    }
  };

  // Handle key deletion
  const handleDeleteKey = async (id: string) => {
    try {
      const wasDeleted = await deleteKey(id);
      // Only refetch if the key was actually deleted
      if (wasDeleted) {
        await fetchKeys();
      }
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <Container>
      <PageHeader title="API Keys" description="Manage your API keys for accessing the Jua API" />

      <div className="bg-white dark:bg-theme-dark-bg shadow-sm rounded-lg">
        <KeysHeader
          onCreateKey={() => setIsCreateModalOpen(true)}
          keyCount={keys.length}
          keyLimit={keyLimit}
          isLoading={isLoading}
        />

        <ErrorAlert error={error?.message} onClose={clearError} />

        <KeysList
          keys={keys}
          isLoading={isLoading}
          onDelete={handleDeleteKey}
          onCancelDelete={cancelDelete}
          pendingDeletions={pendingDeletions}
          deletingKeys={deletingKeys}
          className="block xl:hidden"
        />

        <KeysTable
          keys={keys}
          isLoading={isLoading}
          onDelete={handleDeleteKey}
          onCancelDelete={cancelDelete}
          pendingDeletions={pendingDeletions}
          deletingKeys={deletingKeys}
          className="hidden xl:block"
        />
      </div>

      <CreateKeyModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateKey}
      />

      <ApiKeyCreatedModal keyDetails={newKey} onClose={() => setNewKey(null)} />
    </Container>
  );
}
